import type { StepId } from "@/features/multistep/registry/types";
import { useFormContext, useWatch } from "react-hook-form";

import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { TIPOS_DOCUMENTO, TIPOS_EMPRESA } from "@/config/form-options";

import { useLocations } from "@/hooks/use-location";
import { Paises } from "@/enum/paises.enum";
import { useEffect, useState } from "react";

export function PersonalInfoStep({ stepId }: { stepId: StepId }) {
  const { control, trigger } = useFormContext();
  const [paisId, setPaisId] = useState<string | undefined>();
  const [departamentoId, setDepartamentoId] = useState<string | undefined>();

  useEffect(() => {
    trigger(`${stepId}`);
  }, []);

  const pais = useWatch({ control, name: `${stepId}.pais` });
  const departamento = useWatch({ control, name: `${stepId}.departamento` });
  const tipoDocumento = useWatch({ control, name: `${stepId}.tipoDocumento` });

  useEffect(() => {
    if (pais) {
      console.log("pais :>> ", pais);
      trigger(`${stepId}.departamento`);
    }
  }, [pais]);

  const { paises, departamentos, ciudades } = useLocations(
    paisId,
    departamentoId
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
      {/* Tipo de documento */}
      <FormField
        control={control}
        name={`${stepId}.tipoDocumento`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Tipo de documento</FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value ?? ""}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecciona el tipo de documento" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_DOCUMENTO.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* No. Documento */}
      <FormField
        control={control}
        name={`${stepId}.numeroDocumento`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>No. Documento</FormLabel>
            <FormControl>
              <Input {...field} placeholder="Ingresa el número de documento" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Nombre o razón social */}
      <FormField
        control={control}
        name={`${stepId}.nombreRazonSocial`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Nombre o razón social</FormLabel>
            <FormControl>
              <Input
                {...field}
                placeholder="Ingresa el nombre o razón social"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Dirección principal */}
      <FormField
        control={control}
        name={`${stepId}.direccion`}
        render={({ field }) => (
          <FormItem className="md:col-span-2 lg:col-span-3">
            <FormLabel>Dirección principal</FormLabel>
            <FormControl>
              <Input {...field} placeholder="Ingresa la dirección" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Correo */}
      <FormField
        control={control}
        name={`${stepId}.correo`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Correo electrónico de notificaciones</FormLabel>
            <FormControl>
              <Input {...field} placeholder="example@example.com" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* País */}
      <FormField
        control={control}
        name={`${stepId}.pais`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>País</FormLabel>
            <FormControl>
              <Select
                onValueChange={(label) => {
                  const selected = paises.find((p) => p.label === label);
                  if (selected) {
                    setPaisId(selected.value); // este es el que viaja a tu API
                  }

                  // Si quieres que el form guarde el id real
                  field.onChange(label ?? "");
                }}
                value={field.value ?? ""}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecciona un país" />
                </SelectTrigger>
                <SelectContent>
                  {paises.map((p) => (
                    <SelectItem key={p.value} value={p.label}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Departamento */}
      {pais === Paises.Colombia && (
        <FormField
          control={control}
          name={`${stepId}.departamento`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Departamento</FormLabel>
              <FormControl>
                <Select
                  onValueChange={(label) => {
                    const selected = departamentos.find(
                      (d) => d.label === label
                    );
                    if (selected) {
                      setDepartamentoId(selected.value); // este es el que viaja a tu API
                    }

                    // Si quieres que el form guarde el id real
                    field.onChange(label ?? "");
                  }}
                  value={field.value ?? ""}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Selecciona un departamento" />
                  </SelectTrigger>
                  <SelectContent>
                    {departamentos.map((d) => (
                      <SelectItem key={d.value} value={d.label}>
                        {d.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {/* Ciudad */}
      <FormField
        control={control}
        name={`${stepId}.ciudad`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Ciudad</FormLabel>
            <FormControl>
              {pais === Paises.Colombia && departamento ? (
                <Select
                  onValueChange={field.onChange}
                  value={field.value ?? ""}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Selecciona una ciudad" />
                  </SelectTrigger>
                  <SelectContent>
                    {(ciudades ?? []).map((c) => (
                      <SelectItem key={c.value} value={c.label}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input {...field} placeholder="Ingresa la ciudad" />
              )}
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Tipo de empresa */}
      {tipoDocumento === "NIT" && (
        <FormField
          control={control}
          name={`${stepId}.tipoEmpresa`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tipo de empresa</FormLabel>
              <FormControl>
                <Select
                  onValueChange={field.onChange}
                  value={field.value ?? ""}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Selecciona el tipo de empresa" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_EMPRESA.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
}
